import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Stepper } from "@/components/ui/stepper";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { DashboardItinerary } from "@/lib/models/dashboard";
import { supabase as sharedSupabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useI18n } from '@/hooks/useI18n';
import { useCheckUserExists } from "@/lib/shared-proposal/hooks/useCheckUserExists";
import { SendQuoteStep2Email } from "./SendQuoteStep2Email";
import { SendQuoteStep2 } from "./SendQuoteStep2";
import { QuoteReview } from "./components/QuoteReview";
import { toast } from "@/hooks/useToast";
import { sendQuote, updateItineraryStatus } from "@/services/itinerary.service";

interface SendQuoteEmailFlowProps {
  itinerary: DashboardItinerary;
  onBack: () => void;
  onComplete: (data: { pdfBase64?: string; pdfFilename?: string }) => void;
}

export const SendQuoteEmailFlow = ({
  itinerary,
  onBack,
  onComplete,
}: SendQuoteEmailFlowProps) => {
  const { t } = useI18n();
  const { userData } = useAuth();

  // Check if user exists with contact email
  const { userExists, isLoading: isCheckingUser } = useCheckUserExists(itinerary.contactEmail);

  const [agencyLogoUrl, setAgencyLogoUrl] = useState<string | null>(null);
  const [agencyLogoSmallUrl, setAgencyLogoSmallUrl] = useState<string | null>(null);

  // Fetch agency logo URLs at mount so they are ready when the user selects the logo
  useEffect(() => {
    if (userData?.agency_id) {
      Promise.all([
        sharedSupabase.rpc('get_my_agency_logo' as any),
        sharedSupabase.rpc('get_my_agency_logo_small' as any),
      ]).then(([logoRes, logoSmallRes]) => {
        setAgencyLogoUrl((logoRes.data as string) || null);
        setAgencyLogoSmallUrl((logoSmallRes.data as string) || null);
      });
    }
  }, [userData?.agency_id]);

  // Stepper state
  const [currentStep, setCurrentStep] = useState(1);

  // Modal state
  const [isCreating, setIsCreating] = useState(false);

  // Email sending state
  const [sendEmail, setSendEmail] = useState(true);

  // Email customization states
  const [emailSubject, setEmailSubject] = useState(
    t('itinerary.emailSubjectPlaceholder', { title: itinerary.title })
  );
  const [customMessage, setCustomMessage] = useState('');
  const [logoChoice, setLogoChoice] = useState<'own' | 'agency' | null>(null);

  // PDF states
  const [includePdf, setIncludePdf] = useState(true);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const [pdfBase64, setPdfBase64] = useState<string | null>(null);
  const [pdfFilename, setPdfFilename] = useState<string | null>(null);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const pdfBlobUrlRef = useRef<string | null>(null);

  // Determine sharing method based on user existence
  const sharingMethod = userExists ? 'existing_user' : 'create_user';

  const resolveLogoUrls = (): { logoUrl: string | null; logoSmallUrl: string | null } => {
    if (logoChoice === 'own')    return { logoUrl: userData?.logo_url || null, logoSmallUrl: userData?.logo_small_url || null };
    if (logoChoice === 'agency') return { logoUrl: agencyLogoUrl, logoSmallUrl: agencyLogoSmallUrl };
    return { logoUrl: null, logoSmallUrl: null };
  };

  useEffect(() => {
    if (pdfBase64 !== null) {
      setPdfBase64(null);
      setPdfFilename(null);
      setPdfError(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logoChoice]);

  const generatePdf = async () => {
    setIsPdfGenerating(true);
    setPdfBase64(null);
    setPdfFilename(null);
    setPdfError(null);
    try {
      const { logoUrl, logoSmallUrl } = resolveLogoUrls();

      const payload: Record<string, string> = { itineraryId: itinerary.id };
      if (logoUrl) payload.logoUrl = logoUrl;
      if (logoSmallUrl) payload.logoSmallUrl = logoSmallUrl;
      const { data, error } = await sharedSupabase.functions.invoke('generate-pdf', {
        body: payload,
      });
      if (error || !data?.pdfBase64) {
        setPdfError(error?.message || 'Failed to generate PDF');
      } else {
        setPdfBase64(data.pdfBase64);
        setPdfFilename(data.fileName || `itinerary-${itinerary.id}.pdf`);
      }
    } catch (err) {
      setPdfError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setIsPdfGenerating(false);
    }
  };

  const handlePreviewPdf = () => {
    if (!pdfBase64) return;
    if (pdfBlobUrlRef.current) {
      URL.revokeObjectURL(pdfBlobUrlRef.current);
    }
    const byteChars = atob(pdfBase64);
    const byteArr = new Uint8Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) byteArr[i] = byteChars.charCodeAt(i);
    const blob = new Blob([byteArr], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    pdfBlobUrlRef.current = url;
    window.open(url, '_blank');
  };

  const handleSendQuote = async () => {
    if (sendEmail && includePdf && (!pdfBase64 || !pdfFilename)) {
      return;
    }

    setIsCreating(true);

    try {
      const result = await sendQuote({
        draftId: itinerary.id,
        pdfBase64: sendEmail && includePdf ? pdfBase64 || '' : undefined,
        pdfFilename: sendEmail && includePdf ? pdfFilename || '' : undefined,
        to: itinerary.contactEmail,
        subject: emailSubject,
        shouldSendEmail: sendEmail,
        sharingMethod,
        variables: {
          client_first_name: itinerary.travelRequest?.contact?.firstName || '',
          client_last_name: itinerary.travelRequest?.contact?.lastName || '',
          itinerary_title: itinerary.title,
          start_date: itinerary.startDate,
          end_date: itinerary.endDate,
          custom_message: customMessage,
          payment: getPaymentData(),
          logo_url: resolveLogoUrls().logoUrl || '',
          cancellation_policy_notes: itinerary.cancellationPolicyNotes || '',
        },
      });

      if (result.success) {
        await updateItineraryStatus(itinerary.id, 'sended');

        if (sendEmail) {
          toast({
            title: t('itinerary.quoteSent'),
            description: t('itinerary.quoteSentDesc'),
          });
        } else {
          toast({
            title: t('itinerary.quotePrepared'),
            description: t('itinerary.quotePreparedDescription'),
          });
        }

        onComplete({
          pdfBase64: pdfBase64 || undefined,
          pdfFilename: pdfFilename || undefined,
        });
      } else {
        if (result.errorCode === 'agency_stripe_not_configured') {
          toast({
            title: 'Pagamento non configurato',
            description: result.message || "Il metodo di pagamento dell'agenzia non è stato ancora configurato.",
            variant: "destructive",
          });
        } else {
          toast({
            title: t("common.error"),
            description: t("itinerary.sendQuoteError"),
            variant: "destructive",
          });
        }
      }
    } catch (error) {
      console.error('Error sending quote:', error);
      toast({
        title: t("common.error"),
        description: t("itinerary.sendQuoteError"),
        variant: "destructive",
      });
    } finally {
      setIsCreating(false);
    }
  };

  const getBankTransferDetails = () => {
    const customerName = itinerary?.contactFullName || '';
    const year = new Date().getFullYear();
    return {
      method: 'bank_transfer',
      beneficiary: 'PLACE2BE S.R.L.',
      bank: 'Intesa Sanpaolo',
      iban: 'IT94M0306975823100000005869',
      swiftCode: 'BCITITMM',
      reference: `${customerName} Saldo ${itinerary.title} ${year}`,
    };
  };

  const getPaymentData = () => {
    const bankDetails = getBankTransferDetails();
    return `Recipient: <strong>${bankDetails.beneficiary}</strong><br>
            Bank: <strong>${bankDetails.bank}</strong><br>
            IBAN: <strong>${bankDetails.iban}</strong><br>
            SWIFT CODE: <strong>${bankDetails.swiftCode}</strong><br>
            Reference: <strong>${bankDetails.reference}</strong><br>`;
  };

  // 3 steps: Email config, PDF & Logo, Review
  const steps = [
    { label: t('itinerary.stepEmailConfiguration') },
    { label: t('itinerary.stepLogoAndPdf') },
    { label: t('itinerary.stepReview') }
  ];

  const skippedSteps = !sendEmail ? [2] : [];

  const handleNext = () => {
    if (currentStep === 1 && !sendEmail) {
      setCurrentStep(3);
    } else if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep === 3 && !sendEmail) {
      setCurrentStep(1);
    } else if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    } else {
      onBack();
    }
  };

  const isPdfReady = !isPdfGenerating && pdfBase64 !== null;
  const hasLogoSelected = logoChoice !== null;

  const canProceedFromStep1 = true;
  const canProceedFromStep2 = includePdf ? (hasLogoSelected && isPdfReady) : true;
  const canSend = (() => {
    if (sendEmail) {
      const pdfCondition = includePdf ? (hasLogoSelected && isPdfReady) : true;
      return pdfCondition;
    } else {
      return true;
    }
  })();

  return (
    <div className="space-y-6">
      <Stepper steps={steps} currentStep={currentStep} skippedSteps={skippedSteps} />

      <div className="min-h-[400px]">
        {currentStep === 1 && (
          <SendQuoteStep2Email
            itinerary={itinerary}
            sendEmail={sendEmail}
            onSendEmailChange={setSendEmail}
            emailSubject={emailSubject}
            customMessage={customMessage}
            onEmailSubjectChange={setEmailSubject}
            onCustomMessageChange={setCustomMessage}
            subjectPlaceholder={t('itinerary.emailSubjectPlaceholder', { title: itinerary.title })}
            userExists={userExists}
            isCheckingUser={isCheckingUser}
          />
        )}

        {currentStep === 2 && (
          <SendQuoteStep2
            logoChoice={logoChoice}
            onLogoChoiceChange={setLogoChoice}
            hasOwnLogo={!!userData?.logo_url}
            hasAgencyLogo={!!userData?.agency_id}
            ownLogoUrl={userData?.logo_url}
            ownLogoSmallUrl={userData?.logo_small_url}
            agencyLogoUrl={agencyLogoUrl}
            agencyLogoSmallUrl={agencyLogoSmallUrl}
            isPdfGenerating={isPdfGenerating}
            pdfBase64={pdfBase64}
            pdfFilename={pdfFilename}
            pdfError={pdfError}
            onGeneratePdf={generatePdf}
            onPreviewPdf={handlePreviewPdf}
            includePdf={includePdf}
            onIncludePdfChange={setIncludePdf}
          />
        )}

        {currentStep === 3 && (
          <QuoteReview
            itinerary={itinerary}
            sendEmail={sendEmail}
            emailSubject={emailSubject}
            customMessage={customMessage}
            includePdf={includePdf}
            logoChoice={logoChoice}
            pdfFilename={pdfFilename}
            sharingMethod={sharingMethod}
            contactEmail={itinerary.contactEmail}
          />
        )}
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between pt-6 border-t">
        <Button
          variant="outline"
          onClick={handlePrevious}
          disabled={isCreating}
        >
          <ChevronLeft className="h-4 w-4 mr-2" />
          {currentStep === 1 ? t('common.back') : t('common.previous')}
        </Button>

        {currentStep < 3 && (
          <Button
            onClick={handleNext}
            disabled={
              (currentStep === 1 && !canProceedFromStep1) ||
              (currentStep === 2 && !canProceedFromStep2)
            }
          >
            {t('common.next')}
            <ChevronRight className="h-4 w-4 ml-2" />
          </Button>
        )}

        {currentStep === 3 && (
          <Button
            onClick={handleSendQuote}
            disabled={!canSend || isCreating}
          >
            {isCreating ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {t('common.sending')}
              </>
            ) : (
              t(sendEmail ? 'itinerary.sendQuote' : 'itinerary.prepareQuote')
            )}
          </Button>
        )}
      </div>
    </div>
  );
};
